import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { RefreshCw, ShoppingCart } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";
import { useI18n } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common/PageHeader";
import { EmptyState } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

export default function Purchasing() {
  const { t } = useI18n();
  const { can } = useAuth();
  const qc = useQueryClient();
  const sugg = useQuery({ queryKey: ["suggestions"], queryFn: async () => (await api.get("/purchasing/suggestions")).data });
  const pos = useQuery({ queryKey: ["pos"], queryFn: async () => (await api.get("/purchasing")).data });

  const runPlan = async () => {
    try { const { data } = await api.post("/purchasing/plan"); toast.success(`${t("g2.planned_done")} (${data.created})`); qc.invalidateQueries({ queryKey: ["suggestions"] }); }
    catch (e) { toast.error(apiErrorMessage(e, t)); }
  };
  const createPo = async (s) => {
    try {
      await api.post("/purchasing", { supplier_id: s.supplier_id, suggestion_ids: [s.id],
        line_items: [{ product_id: s.product_id, description: s.product_name, quantity: s.suggested_qty }] });
      toast.success(t("g2.created")); qc.invalidateQueries({ queryKey: ["suggestions"] }); qc.invalidateQueries({ queryKey: ["pos"] });
    } catch (e) { toast.error(apiErrorMessage(e, t)); }
  };
  const transition = async (po, action) => {
    try { await api.post(`/purchasing/${po.id}/transition`, { action }); toast.success(t("g2.action_done")); qc.invalidateQueries({ queryKey: ["pos"] }); }
    catch (e) { toast.error(apiErrorMessage(e, t)); }
  };
  const receiveAll = async (po) => {
    try {
      const { data } = await api.get(`/purchasing/${po.id}`);
      const lines = data.line_items.map((l) => ({ po_line_id: l.id, quantity: String(Number(l.quantity) - Number(l.received_qty)) })).filter((l) => Number(l.quantity) > 0);
      await api.post(`/purchasing/${po.id}/receive`, { lines });
      toast.success(t("g2.action_done")); qc.invalidateQueries({ queryKey: ["pos"] }); qc.invalidateQueries({ queryKey: ["balances"] });
    } catch (e) { toast.error(apiErrorMessage(e, t)); }
  };

  return (
    <div>
      <PageHeader title={t("nav.purchasing")} subtitle={t("g2.purchasing_sub")}
        actions={can("purchasing:write") && <Button onClick={runPlan} data-testid="plan-button"><RefreshCw className="h-4 w-4" />{t("g2.plan")}</Button>} />

      <h3 className="mb-2 font-heading text-sm font-semibold uppercase tracking-wide text-slate-500">{t("g2.suggestions")}</h3>
      <Card className="mb-6">
        {sugg.data && sugg.data.length > 0 ? (
          <Table data-testid="suggestions-table">
            <TableHeader><TableRow><TableHead>{t("g2.product")}</TableHead><TableHead>{t("g2.supplier")}</TableHead><TableHead className="text-right">{t("g2.shortage")}</TableHead><TableHead className="text-right">{t("g2.suggested")}</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {sugg.data.map((s) => (
                <TableRow key={s.id} data-testid={`suggestion-row-${s.id}`}>
                  <TableCell className="font-medium">{s.product_name}</TableCell>
                  <TableCell>{s.supplier_name || "—"}</TableCell>
                  <TableCell className="text-right tabular-nums text-red-600">{s.shortage_qty}</TableCell>
                  <TableCell className="text-right tabular-nums">{s.suggested_qty}</TableCell>
                  <TableCell className="text-right">{can("purchasing:write") && <Button size="sm" onClick={() => createPo(s)} data-testid={`create-po-${s.id}`}>{t("g2.create_po")}</Button>}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : <EmptyState icon={ShoppingCart} title={t("g2.no_suggestions")} testId="suggestions-empty" />}
      </Card>

      <h3 className="mb-2 font-heading text-sm font-semibold uppercase tracking-wide text-slate-500">{t("g2.purchase_orders")}</h3>
      <Card>
        {pos.data && pos.data.length > 0 ? (
          <Table data-testid="pos-table">
            <TableHeader><TableRow><TableHead>{t("orders.number")}</TableHead><TableHead>{t("g2.supplier")}</TableHead><TableHead>{t("common.status")}</TableHead><TableHead className="text-right">{t("common.actions")}</TableHead></TableRow></TableHeader>
            <TableBody>
              {pos.data.map((po) => (
                <TableRow key={po.id} data-testid={`po-row-${po.id}`}>
                  <TableCell className="font-medium tabular-nums">{po.po_number}</TableCell>
                  <TableCell>{po.supplier_name}</TableCell>
                  <TableCell><span className="text-xs">{po.status}</span></TableCell>
                  <TableCell className="text-right space-x-1">
                    {po.status === "draft" && can("purchasing:approve") && <Button size="sm" variant="outline" onClick={() => transition(po, "approve")} data-testid={`po-approve-${po.id}`}>{t("g2.approve")}</Button>}
                    {po.status === "approved" && <Button size="sm" variant="outline" onClick={() => transition(po, "send")} data-testid={`po-send-${po.id}`}>{t("g2.send")}</Button>}
                    {po.status === "sent" && <Button size="sm" variant="outline" onClick={() => transition(po, "confirm")} data-testid={`po-confirm-${po.id}`}>{t("g2.confirm")}</Button>}
                    {["sent", "confirmed", "partially_received"].includes(po.status) && can("purchasing:write") && <Button size="sm" onClick={() => receiveAll(po)} data-testid={`po-receive-${po.id}`}>{t("g2.receive_all")}</Button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : <EmptyState icon={ShoppingCart} title={t("g2.purchase_orders")} testId="pos-empty" />}
      </Card>
    </div>
  );
}
